// client/src/components/PortfolioStockHeader.js
import React, { useState } from 'react';
import SortingControl from './SortingControl';

const PortfolioStockHeader = ({ 
    searchTerm, 
    onSearch, 
    sortBy, 
    onSortChange, 
    sortOrder, 
    onSortOrderChange,
    isSearching,
    searchResults,
    onStockSelect,
    portfolio = [], 
    onFilterChange 
}) => {
    const [activeFilter, setActiveFilter] = useState(null);

    // Predefined filters for value investors
    const valueFilters = [
        {
            id: 'all',
            label: 'Alle',
            icon: 'fa-th-list',
            description: 'Zeigt alle Aktien',
            filterFn: (stocks) => stocks
        },
        {
            id: 'best_month',
            label: 'Top 30T',
            icon: 'fa-chart-line',
            description: 'Zeigt die Top-Performer des letzten Monats',
            filterFn: (stocks) => {
                return [...stocks].sort((a, b) => b.monthly_change - a.monthly_change).slice(0, 10);
            }
        },
        {
            id: 'best_week',
            label: 'Top 7T',
            icon: 'fa-arrow-trend-up',
            description: 'Zeigt die Top-Performer der letzten Woche',
            filterFn: (stocks) => {
                return [...stocks].sort((a, b) => b.weekly_change - a.weekly_change).slice(0, 10);
            }
        },
        {
            id: 'undervalued',
            label: 'Niedriges KGV',
            icon: 'fa-tags',
            description: 'Aktien mit niedrigem KGV (unter 15)',
            filterFn: (stocks) => {
                return stocks.filter(stock => stock.trailing_p_e && stock.trailing_p_e < 15);
            }
        },
        {
            id: 'book_value',
            label: 'Niedriges KBV',
            icon: 'fa-book',
            description: 'Aktien mit niedrigem KBV (unter 1.5)',
            filterFn: (stocks) => {
                return stocks.filter(stock => stock.price_to_book && stock.price_to_book < 1.5);
            }
        },
        {
            id: 'high_dividend',
            label: 'Dividende',
            icon: 'fa-coins',
            description: 'Aktien mit hoher Dividendenrendite (über 3%)',
            filterFn: (stocks) => {
                return stocks.filter(stock => stock.dividend_yield && stock.dividend_yield > 3);
            }
        },
        {
            id: 'quality_growth',
            label: 'Wachstum',
            icon: 'fa-seedling',
            description: 'Hohe Gewinnmarge und gutes Wachstum',
            filterFn: (stocks) => {
                return stocks.filter(stock => 
                    stock.profit_margin > 0.15 && 
                    stock.growth_estimates?.next_year > 0.10
                );
            }
        }
    ];

    
    // Function to apply a filter
    const applyFilter = (filter) => {
        // Determine appropriate sort criteria based on filter
        const getSortCriteriaForFilter = (filterId) => {
            switch(filterId) {
                case 'best_month':
                    return { sortBy: 'monthly_change', sortOrder: 'desc' };
                case 'best_week':
                    return { sortBy: 'weekly_change', sortOrder: 'desc' };
                case 'undervalued':
                    return { sortBy: 'trailing_p_e', sortOrder: 'asc' };
                case 'book_value':
                    return { sortBy: 'price_to_book', sortOrder: 'asc' };
                case 'high_dividend':
                    return { sortBy: 'dividend_yield', sortOrder: 'desc' };
                case 'quality_growth':
                    return { sortBy: 'profit_margin', sortOrder: 'desc' };
                default:
                    return { sortBy: 'total_growth', sortOrder: 'desc' };
            }
        };
    
        if (activeFilter?.id === filter.id && filter.id !== 'all') {
            // If clicking the active filter, deactivate it
            setActiveFilter(valueFilters[0]); // Set to "All" filter
            onFilterChange(null);
            
            // Reset to default sorting
            onSortChange('total_growth');
            onSortOrderChange('desc');
        } else {
            // Apply the new filter
            setActiveFilter(filter);
            const filteredStocks = filter.filterFn(portfolio);
            onFilterChange(filteredStocks);
            
            // Update sorting based on filter
            const { sortBy, sortOrder } = getSortCriteriaForFilter(filter.id);
            onSortChange(sortBy);
            onSortOrderChange(sortOrder);
        }
    };

    // If no filter is active, set "All" as default
    React.useEffect(() => {
        if (!activeFilter && valueFilters.length > 0) {
            setActiveFilter(valueFilters[0]);
        }
    }, [activeFilter]);

    return (
        <div className="portfolio-stock-header">
            <div className="header-top">
                <h2 className="section-title">Meine Aktien</h2>
                
                <div className="portfolio-search">
                    <div className="search-input-container">
                        <input
                            type="text"
                            className="modern-input"
                            placeholder="Aktie hinzufügen..."
                            value={searchTerm}
                            onChange={(e) => onSearch(e.target.value)}
                        />
                        <button className="add-stock-btn">
                            <i className="fas fa-plus"></i>
                        </button>
                        {isSearching && <div className="search-spinner" />}
                    </div>
                    {searchResults.length > 0 && (
                        <div className="search-results-dropdown">
                            {searchResults.map(stock => (
                                <div
                                    key={stock.id}
                                    className="search-result-item"
                                    onClick={() => onStockSelect(stock)}
                                >
                                    <div className="stock-name">{stock.name}</div>
                                    {stock.sector && (
                                        <div className="stock-sector">{stock.sector}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="filter-scroll-container">
                <div className="filter-cards">
                    {valueFilters.map(filter => (
                        <div 
                            key={filter.id} 
                            className={`filter-card ${activeFilter?.id === filter.id ? 'active' : ''}`}
                            onClick={() => applyFilter(filter)}
                        >
                            <div className="filter-card-icon">
                                <i className={`fas ${filter.icon}`}></i>
                            </div>
                            <div className="filter-card-content">
                                <span className="filter-card-label">{filter.label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="sorting-controls">
                <SortingControl
                    sortBy={sortBy}
                    onSortChange={onSortChange}
                    sortOrder={sortOrder}
                    onSortOrderChange={onSortOrderChange}
                />
            </div>
        </div>
    );
};

export default PortfolioStockHeader;